import type { components } from './schema';
import { layoutToSpreadId } from './mappers';
import { spreadTypes, t } from '@/i18n';
import type { SpreadTypeId, TarotCardItem } from '@/types';

type UserProfile = components['schemas']['UserProfileResponse'];
type LayoutTypeString = components['schemas']['LayoutTypeString'];

const prismaLayoutToSpreadId: Record<string, SpreadTypeId> = {
  THREE_CARDS: 'classic-3',
  THREE_CARD: 'classic-3',
  CROSS: 'cross-4',
  CELTIC_CROSS: 'cross-4',
  HORSE_SHOE: 'horseshoe',
  CUSTOM: 'auto-photo',
};

/**
 * Total readings available to the user — sum of free credits and subscription
 * credits when the business subscription is active. Single source of truth for
 * the balance shown in sidebars / profile header.
 */
export function getSpreadsRemaining(user: UserProfile): number {
  const business = user.businessActive ? user.businessPhotoReadings : 0;
  return (user.photoReadings ?? 0) + business;
}

export function getDisplayName(user: UserProfile): string {
  const first = user.firstName?.trim();
  const last = user.lastName?.trim();
  const full = [first, last].filter(Boolean).join(' ');
  if (full) return full;
  if (user.username?.trim()) return user.username;
  if (user.email?.trim()) return user.email.split('@')[0];
  return t.common.userFallback;
}

export function getEmailOrHandle(user: UserProfile): string {
  if (user.email) return user.email;
  if (user.username) return `@${user.username}`;
  return '';
}

/** Maps backend layout label to a localized spread name when possible. */
export function formatReadingLayoutLabel(
  layoutType: string | null | undefined,
  readingType?: string | null,
): string {
  if (layoutType) {
    const normalized = layoutType.trim();
    const prismaKey = normalized as keyof typeof t.profile.history.layouts;
    if (prismaKey in t.profile.history.layouts) {
      return t.profile.history.layouts[prismaKey];
    }

    const spreadFromPrisma = prismaLayoutToSpreadId[normalized];
    if (spreadFromPrisma) {
      const match = spreadTypes.find((item) => item.id === spreadFromPrisma);
      if (match) return match.name;
    }

    const spreadId = layoutToSpreadId[normalized as LayoutTypeString];
    if (spreadId) {
      const match = spreadTypes.find((item) => item.id === spreadId);
      if (match) return match.name;
    }
  }

  if (readingType) {
    return formatReadingTypeLabel(readingType);
  }

  return t.profile.packageFallback;
}

export function formatReadingTypeLabel(readingType: string): string {
  const key = readingType.trim() as keyof typeof t.profile.history.readingTypes;
  if (key in t.profile.history.readingTypes) {
    return t.profile.history.readingTypes[key];
  }
  return readingType.replace(/_/g, ' ').toLowerCase();
}

export function stripHtmlForPreview(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Defensive adapter for the free-form `cards` JSON on a `ReadingDetailResponse`.
 * The backend stores an AI-produced payload whose exact shape isn't captured in
 * the OpenAPI spec; we accept any reasonable shape and fall back to an empty
 * array so the UI never crashes on an unknown payload.
 */
export function toTarotCardItems(cards: unknown): TarotCardItem[] {
  if (!Array.isArray(cards)) return [];
  return cards
    .map((raw): TarotCardItem | null => {
      if (!raw || typeof raw !== 'object') return null;
      const obj = raw as Record<string, unknown>;
      const name = pickString(obj, ['name', 'title', 'cardName', 'card']);
      if (!name) return null;
      return {
        emoji: pickString(obj, ['emoji', 'icon']) ?? String.fromCodePoint(0x1f0cf),
        name,
        description:
          pickString(obj, ['description', 'interpretation', 'meaning', 'summary']) ?? '',
      };
    })
    .filter((x): x is TarotCardItem => x !== null);
}

function pickString(obj: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  return null;
}
